import type { FormKitTypeDefinition } from '@formkit/core'
import { clone, cloneAny } from '@formkit/utils'
import { twMerge } from 'tailwind-merge'
import { EditorControl, Variable, controlScales } from './editor'
import { rootClasses } from './rootClasses'

declare global {
  var __FORMKIT_THEME__: Theme<ThemeOptions> | undefined
}

type Inputs = {
  [inputName: string]: {
    [sectionName: string]: string
  }
}

export type Globals = {
  /**
   * Use globals to add classes should be applied to a matching section no
   * matter what input type is being used. These classes will be merged with
   * any classes defined for a matching specific input type and section, with
   * the more specific input type classes taking precedence (uses
   * tailwind-merge).
   */
  __globals?: {
    [sectionName: string]: string
  }
}

export type InputMap = Inputs & Globals

export type ExtractVariables<T extends ThemeOptions> = {
  [P in keyof T['variables']]: string
}

export interface ThemeOptions {
  meta: {
    /**
     * The name of your theme.
     */
    name: string
    /**
     * Description of your theme.
     */
    description: string
    /**
     * Flag to indicate if your theme supports dark mode.
     */
    darkMode: boolean
    /**
     * An explicit list of formkit inputs that are supported by this theme.
     * This should include both pro and no pro inputs.
     */
    supportedInputs: string[]
    /**
     * What versions of FormKit does your theme support? You can use a semver
     * range to indicate a range of versions.
     *
     * @example "^1.2.1"
     */
    formKitVersion: string
    /**
     * The author’s to be displayed in the theme gallery.
     */
    authorName?: string
    /**
     * The author’s website to be displayed in the theme gallery.
     */
    authorWebsite?: string
  }
  /**
   * A map of variables that can be customized by the user of this theme. These
   * variables can then be used by prefixing a dollar sign `$` to the variable
   * name and using it in any of the input classes.
   *
   * ```js
   * {
   *   variables: {
   *     primaryColor: {
   *       editor: 'color',
   *       value: 'blue',
   *     }
   *   },
   *   inputs: {
   *     text: {
   *      inner: 'border-$primaryColor-500',
   *      input: 'bg-$primaryColor-50',
   *     }
   *   }
   * }
   * ```
   */
  variables: Record<string, Variable>
  /**
   * A map of classes for each input type. The input type is the key and then
   * the value is a map of sections to their corresponding classes.
   */
  inputs: InputMap
}

export interface Theme<T extends ThemeOptions> {
  (options?: Partial<ExtractVariables<T>>, classes?: InputMap): {
    tailwind: (
      inputs?: string[] | Record<string, FormKitTypeDefinition>
    ) => InputMap
    editorControls: () => Record<string, EditorControl & { scale: string[] }>
  }
  meta: T['meta']
}

const VARIABLE_RE = /\$(?<var>[a-z0-9_]+)(?:\((?<arg>.+?)\))?/gi

const VARIABLE_REMOVE_RE = /\$remove:(?<className>[^\s]+)(?:\s|$)/gi

/**
 * Merge the provided classes into the input map using tailwind-merge. This
 * allows for the user to override the default classes for any given input
 * section (modifies first argument by reference).
 *
 * @param inputMap - The input map to be modified
 * @param classes - The classes to merge
 */
function mergeClasses(inputMap: InputMap, classes: InputMap): void {
  const inputs = Object.keys(inputMap)
  for (const input of inputs) {
    const sections = inputMap[input]
    const overriddenSections = input in classes ? { ...classes[input] } : {}
    const sectionNames = Object.keys(sections).concat(
      Object.keys(overriddenSections)
    )
    for (const sectionName of sectionNames) {
      let classList = [
        ...new Set(
          twMerge(
            inputMap.__globals?.[sectionName] ?? '',
            inputMap[input][sectionName] ?? '',
            overriddenSections[sectionName] ?? ''
          ).split(' ')
        ),
      ].join(' ')
      if (classList.includes('$reset')) {
        classList = classList.substring(classList.indexOf('$reset') + 6)
      }
      if (classList.includes('$remove:')) {
        const matches = classList.matchAll(VARIABLE_REMOVE_RE)
        for (const match of matches) {
          const matchedString = match[0]
          const className = match.groups?.className
          if (className) {
            classList = classList.replace(matchedString, '')
            classList = classList.replace(`${className} `, '')
          }
        }
      }
      inputMap[input][sectionName] = classList
        .trim()
        .replace(/\s+/g, ' ')
        // replace quotes in attribute selectors. eg [disabled="true"] becomes [disabled=true]
        .replace(/\[(\w+)=["'](.+?)["']\]/g, '[$1=$2]')
    }
  }
}

/**
 * Merge the provided options into the theme variables. This allows for the
 * user to override the default classes for properties the theme author has
 * defined as "variable".
 *
 * @param themeVariables - The theme variables
 * @param options - The options to merge
 * @returns The merged variables
 */
function mergeVariables(
  themeVariables: Record<string, Variable>,
  options?: Partial<Record<string, string>>
): Record<string, string> {
  const mergedVariables: Record<string, string> = {}
  const variableNames = Object.keys(themeVariables)
  options ??= {}
  for (const variableName of variableNames) {
    if (variableName in options) {
      mergedVariables[variableName] = options[variableName] as string
      continue
    }
    const variable = themeVariables[variableName]
    mergedVariables[variableName] =
      typeof variable === 'object' ? variable.value : variable
  }
  return mergedVariables
}

/**
 * Apply the provided variables to one or more input maps (by reference).
 * @param variables - The variables to apply
 * @param inputMaps - The input maps to apply the variables to
 */
function applyVariables(
  variables: ThemeOptions['variables'],
  mergedVars: Record<string, string>,
  ...inputMaps: Array<InputMap | undefined>
): void {
  if (inputMaps.length > 1) {
    inputMaps.forEach((inputMap) =>
      applyVariables(variables, mergedVars, inputMap)
    )
    return
  }
  const editorControls = getControls(variables, mergedVars)
  const inputMap = inputMaps[0]
  if (!inputMap || typeof inputMap !== 'object') return
  const inputs = Object.keys(inputMap)

  // This is just a boring nested loop to replace all mergedVars in the classes
  for (const input of inputs) {
    const sectionNames = Object.keys(inputMap[input])
    for (const section of sectionNames) {
      inputMap[input][section] = replaceVariables(
        inputMap[input][section],
        mergedVars,
        editorControls
      )
    }
  }
}

function replaceVariables(
  classString: string,
  mergedVars: Record<string, string>,
  editorControls: Record<string, EditorControl>
): string {
  const variableMatches = classString.matchAll(VARIABLE_RE)

  for (const match of variableMatches) {
    const variableName = match.groups?.var
    if (!variableName || variableName === 'remove' || variableName === 'reset')
      continue
    if (!(variableName in mergedVars)) {
      throw new Error(
        `Unknown theme variable found in FormKit theme.
Found $${variableName} in "${classString}", but no variable with that name was defined on the theme.`
      )
    }
    const matchedString = match[0]
    let variableValue = mergedVars[variableName]
    if (match.groups?.arg && typeof editorControls[variableName]) {
      const control = editorControls[variableName]
      const [argShift, argMin, argMax] = match.groups.arg
        .split(',')
        .map((a) => a.trim())
      const valueScale =
        'scale' in control ? control.scale : controlScales[control.editor]

      // Get the index of the minimum and maximum allowed values in the scale
      let definedMin =
        argMin ?? ('min' in control ? control.min : valueScale[0])
      let definedMax =
        argMax ??
        ('max' in control ? control.max : valueScale[valueScale.length - 1])

      // If the first argument is `*` allow any
      if (definedMin === '*') {
        definedMin = valueScale[0]
        definedMax = valueScale[valueScale.length - 1]
      }

      const controlMin = (valueScale as any).indexOf(definedMin)
      if (controlMin === -1) {
        throw new Error(
          `Min $${variableName} "${definedMin}" is not in the scale: [${valueScale.join(
            ', '
          )}].`
        )
      }
      const controlMax = (valueScale as any).indexOf(definedMax)
      if (controlMax === -1) {
        throw new Error(
          `Max $${variableName} "${definedMax}" is not in the scale: [${valueScale.join(
            ', '
          )}].`
        )
      }

      let valueIndex = (valueScale as any).indexOf(
        editorControls[variableName].value
      )
      const shiftBy = parseInt(argShift)

      // Clamp to the min and max of the value scale
      valueIndex = Math.min(
        Math.max(controlMin, valueIndex + shiftBy),
        controlMax
      )
      variableValue = valueScale[valueIndex]
    }
    classString = classString.replace(matchedString, variableValue)
  }
  return classString
}

/**
 * Generate a tailwind class theme for FormKit inputs.
 * @param themeDefinition - The theme definition
 * @param options - The theme options
 * @param classes - The input classes
 * @param inputs - The inputs to generate classes for
 * @returns The input map of classes
 */
function generateTailwind<T extends ThemeOptions>(
  themeDefinition: T,
  options?: Partial<ExtractVariables<T>>,
  classes?: InputMap,
  inputs?: string[] | Record<string, FormKitTypeDefinition>
): InputMap {
  const variables = mergeVariables(themeDefinition.variables, options)
  const inputMap = reduceInputs(themeDefinition.inputs, inputs)
  applyVariables(themeDefinition.variables, variables, inputMap, classes)
  mergeClasses(inputMap, classes ?? {})
  return inputMap
}

/**
 * Given an input map of classes, reduce the set to only the inputs that are
 * being requested.
 * @param classes - The input map of classes
 * @param inputs - The inputs to reduce to
 * @returns The reduced input map
 */
function reduceInputs(
  classes: InputMap,
  inputs?: string[] | Record<string, FormKitTypeDefinition>
): InputMap {
  const inputMap = clone(classes)
  if (inputs) {
    inputs = Array.isArray(inputs) ? inputs : Object.keys(inputs)
    return inputs.reduce(
      (acc, input) => {
        acc[input] = input in inputMap ? inputMap[input] : {}
        return acc
      },
      { __globals: inputMap['__globals'] } as InputMap
    )
  }
  return inputMap
}

/**
 * Get the editor control variables.
 * @param themeDefinition - The theme definition
 * @param options - The theme options
 */
function getControls(
  variables: ThemeOptions['variables'],
  options: Record<string, string>
): Record<string, EditorControl & { scale: string[] }> {
  const values = mergeVariables(variables, options)
  const vars = Object.keys(variables)
  return vars.reduce((acc, variable) => {
    const control = variables[variable] ? cloneAny(variables[variable]) : false
    if (typeof control === 'object' && 'editor' in control) {
      control['value'] = values[variable] as EditorControl['value']
      acc[variable] = {
        ...control,
        scale: ('scale' in control
          ? control.scale
          : controlScales[control.editor]) as unknown as string[],
      }
    }
    return acc
  }, {} as Record<string, EditorControl & { scale: string[] }>)
}

/**
 * Export the control scales for use in the editor.
 */
export { controlScales, rootClasses }

/**
 * Export the editor control type.
 */
export type { EditorControl }

/**
 * Create a new FormKit theme. Themes created with this function can be used to
 * generate tailwind class themes for FormKit inputs or an equivalent
 * standard CSS stylesheet.
 * @param themeDefinition - The theme definition
 * @returns The theme
 */
export function createTheme<T extends ThemeOptions>(
  themeDefinition: T
): Theme<T> {
  const theme: Theme<T> = function (variables, classes) {
    return {
      tailwind: (inputs?: string[] | Record<string, FormKitTypeDefinition>) =>
        generateTailwind(themeDefinition, variables, classes, inputs),
      editorControls: () =>
        getControls(
          themeDefinition.variables,
          variables as Record<string, string>
        ),
    }
  }
  if (typeof window !== 'undefined') {
    // Notify any listening editors that this is the current theme
    globalThis.__FORMKIT_THEME__ = theme as Theme<ThemeOptions>
    document.dispatchEvent(new Event('FormKitTheme'))
  }
  theme.meta = themeDefinition.meta
  return theme
}
